import type { BaseHit, Hit } from 'instantsearch.js'
import { useMemo } from 'react'
import type { HitsProps } from 'react-instantsearch'
import { t } from '../../../../assets/utils/algolia/algoliaLocalization'
import CardHitComponent from './CardHitComponent'
import { useConfigContext } from './ConfigContext'
import ListHitComponent from './ListHitComponent'
import { commaList } from '../../../../assets/utils/format'
import useReplaceImgixUrl from '../../../../assets/utils/useReplaceImgixUrl'

export type HitComponentProps = Omit<
  NonNullable<
    HitsProps<BaseHit>['hitComponent']
  > extends React.JSXElementConstructor<infer P>
    ? P
    : never,
  'sendEvent'
>

export type HitComponentTemplateProps = HitComponentProps & {
  isNew: boolean
  speakers: string[]
  detailUrl: string
  imageUrl: string
  imageAlt: string
  imageTitle: string
  teaser: React.ReactNode
}

function isSermonNew(item: Hit<BaseHit>) {
  const eventPublishTimestamp = item.publishAt
  const sermonPublishTimestamp = item.publishAt
  const sermonDate = item.date
  const nowTimestamp = Date.now()
  const oneWeekAgo = nowTimestamp - 7 * 24 * 60 * 60 * 1000
  return sermonPublishTimestamp >= oneWeekAgo || sermonDate >= oneWeekAgo
}

type Speaker = {
  firstName: Record<string, unknown>
  fullName: Record<string, unknown>
  lastName: Record<string, unknown>
}

export default function HitComponent({
  hit,
  variant
}: HitComponentProps & { variant: 'card' | 'list' }) {
  const { baseUrl, imgixSrcUrl, imgixTargetUrl } = useConfigContext()

  const isNew = isSermonNew(hit)

  let rawDetailUrl = `${baseUrl}${t(hit.slug)}`
  const detailUrl = rawDetailUrl.endsWith('/')
    ? rawDetailUrl
    : `${rawDetailUrl}/`

  // Fall back to the series image as a whole, so alt text and title stay in sync with the URL
  const image = hit.image?.url ? hit.image : hit.series?.image

  const imageUrl = t(image?.url, { fallback: 'any' })
  const imgixUrl = useReplaceImgixUrl(imgixSrcUrl, imgixTargetUrl)(imageUrl)
  const imageAlt = t(image?.altText, { fallback: 'none' })
  const imageTitle = t(image?.title, { fallback: 'none' })

  const speakers = commaList(
    (hit.speakers ?? [])
      .filter(Boolean)
      .map((s: Record<string, Speaker>) => t(s.fullName, { fallback: 'any' }))
  )

  // Hub-side limited to ~100 chars; older index objects only carry it after the next rebuild
  const teaser = t(hit.excerpt, { fallback: 'any' })

  const props = {
    isNew,
    speakers,
    detailUrl,
    imageUrl: imgixUrl,
    imageAlt,
    imageTitle,
    teaser
  }

  if (variant === 'list') {
    return <ListHitComponent hit={hit} {...props} />
  }

  if (variant === 'card') {
    return <CardHitComponent hit={hit} {...props} />
  }

  return null
}
